import { IoCloudDownloadOutline } from "react-icons/io5";
import "./StoreQrModal.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose, MdQrCodeScanner } from "react-icons/md";
import {
  Document,
  Page,
  PDFDownloadLink,
  StyleSheet,
  Text,
  View,
  Image,
  Link,
} from "@react-pdf/renderer";
import { Button, Modal } from "rsuite";
import logo from "../../../../assets/images/easypaylogoblack.png";
import ondcLogo from "../../../../assets/images/ONDC_logo.png";
import { QRCodeCanvas } from "qrcode.react";

export const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    alignItems: "center",
    padding: 30,
    backgroundColor: "#fff",
  },
  cardWrapper: {
    borderTopWidth: 16,
    borderBottomWidth: 16,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderColor: "#2e3c7e",
    borderStyle: "solid",
    borderRadius: 36,
    padding: 20,
    width: "60%",
    alignItems: "center",
    textAlign: "center",
  },
  logoRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    marginTop: 10,
  },
  logo: {
    width: 100,
    height: 40,
    marginHorizontal: 5,
  },
  qrCode: {
    width: 150,
    height: 150,
    marginBottom: 10,
  },
  storeName: {
    fontSize: 16,
    paddingBottom: "15px",
    paddingTop: "15px",
    fontWeight: "700",
  },
  scanQrText: {
    fontSize: 14,
    color: "#666666",
    paddingBottom: "15px",
    fontWeight: "700",
  },
  catologText: {
    fontSize: 12,
    color: "#666666",
    paddingTop: "15px",
  },
  storeLink: {
    fontSize: 12,
    color: "blue",
    textDecoration: "underline",
    paddingTop: "10px",
  },
});

// Rendered through @react-pdf/renderer's own reconciler (not react-dom), which
// doesn't implement useSyncExternalStore - so useTranslation() would crash here.
// Translated strings must be computed by the caller and passed in as plain props.
const StorePdfDocument = ({ storeList, qrDataUrl, labels }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.cardWrapper}>
        <Text style={styles.storeName}>{storeList?.storeName}</Text>
        <Text style={styles.scanQrText}>{labels.scanQrCode}</Text>
        {qrDataUrl && storeList?.storeLink && (
          <Link src={storeList.storeLink}>
            <Image style={styles.qrCode} src={qrDataUrl} />
          </Link>
        )}
        <Text style={styles.catologText}>{labels.catalogPoweredBy}</Text>
        <View style={styles.logoRow}>
          <Image src={logo} style={styles.logo} />
          <Image src={ondcLogo} style={styles.logo} />
        </View>
      </View>
      {storeList?.storeLink && (
        <Link src={storeList.storeLink} style={styles.storeLink}>
          {labels.clickToOpenStore}
        </Link>
      )}
    </Page>
  </Document>
);

const StoreQrModal = ({ storeList, onClose, isQrModalVisible }) => {
  const { t } = useTranslation();
  const qrRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  useEffect(() => {
    if (isQrModalVisible) {
      setTimeout(() => {
        const canvas = document.getElementById("qr-canvas");
        if (canvas) {
          setQrDataUrl(canvas.toDataURL("image/png"));
        }
      }, 300);
    }
  }, [isQrModalVisible]);

  return (
    <Modal
      open={isQrModalVisible}
      onClose={onClose}
      keyboard={false}
      className="store-qr-modal"
    >
      <div className="store-close-button" onClick={onClose}>
        <MdClose />
      </div>
      <Modal.Header>
        <div className="store-qr-modal-header">
          <div className="store-qr-title-container">
            <MdQrCodeScanner className="store-qr-icon" />
            <Modal.Title className="store-qr-title">
              {t("qrModal.title")}
            </Modal.Title>
          </div>
          <Button
            appearance="primary"
            className="store-download-button"
            disabled={!storeList}
          >
            {qrDataUrl && (
              <PDFDownloadLink
                document={
                  <StorePdfDocument
                    storeList={storeList}
                    qrDataUrl={qrDataUrl}
                    labels={{
                      scanQrCode: t("qrModal.scanQrCode"),
                      catalogPoweredBy: t("qrModal.catalogPoweredBy"),
                      clickToOpenStore: t("qrModal.clickToOpenStore"),
                    }}
                  />
                }
                fileName={`${storeList?.storeName || "catalog"}.pdf`}
                style={{ textDecoration: "none" }}
              >
                {({ loading }) => (
                  <Button
                    appearance="primary"
                    className="store-download-button"
                    startIcon={<IoCloudDownloadOutline />}
                  >
                    {loading ? t("qrModal.preparing") : t("qrModal.download")}
                  </Button>
                )}
              </PDFDownloadLink>
            )}
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body ref={qrRef}>
        {storeList?.storeLink ? (
          <div className="store-qr-card ">
            <div className="store-qr-primary-text">{storeList?.storeName}</div>
            <div className="store-qr-secondary-text">
              {t("qrModal.scanQrCode")}
            </div>
            <div className="store-qr-code-container">
              <a
                href={storeList?.storeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="qr-link"
              >
                <QRCodeCanvas
                  id="qr-canvas"
                  value={storeList?.storeLink}
                  size={180}
                  level="H"
                  imageSettings={{
                    src: storeList?.buyerLogoBase64,
                    height: 70,
                    width: 70,
                    excavate: true,
                  }}
                />

                <span className="store-qr-hover-text">
                  {t("qrModal.scanOrClick")}
                </span>
              </a>
            </div>
            <div className="store-qr-footer">
              <span>{t("qrModal.catalogPoweredBy")}</span>
              <div className="store-qr-footer-logos">
                <img src={logo} alt="Easy Pay" />
                <img src={ondcLogo} alt="ONDC" />{" "}
              </div>
            </div>
          </div>
        ) : (
          <div className="store-not-qr-text">
            {t("qrModal.notGenerated")}
          </div>
        )}
        {storeList?.storeLink && (
          <div className="store-qr-share-text">
            {t("qrModal.shareText")}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default StoreQrModal;
